/**
 * Contact details in product text: phone numbers, WhatsApp/Telegram/social
 * links and handles, emails and "PM me"-style requests. Sellers are not to put
 * these in product titles or descriptions - buyers reach them through FishyHub.
 *
 * The same rules live in merchant-backend (refuses the save), merchant-frontend
 * (warns while typing) and web-backend (hides them when a description is
 * shown); keep the three copies in step.
 *
 * Ordinary links (a manufacturer's manual, a YouTube video) are allowed.
 */

// Messaging, social and marketplace sites, by host.
const CONTACT_HOSTS =
  /\b(?:https?:\/\/)?(?:www\.)?(?:wa\.me|wa\.link|(?:api|chat|web)\.whatsapp\.com|whatsapp\.com|t\.me|telegram\.(?:me|org|dog)|instagram\.com|instagr\.am|facebook\.com|fb\.com|fb\.me|m\.me|tiktok\.com|vm\.tiktok\.com|twitter\.com|x\.com|threads\.net|carousell\.(?:sg|com|com\.my|ph)|shopee\.(?:sg|com|com\.my|co\.id|ph|tw|vn)|lazada\.(?:sg|com|com\.my|co\.id)|line\.me|linktr\.ee|wechat\.com|weixin\.qq\.com)(?:\/[^\s<"']*)?/gi;

// The apps named, or a handle given for them ("IG: fishroom", "Telegram @x").
const CONTACT_APPS =
  /\b(?:what'?s?\s?h?app|telegram|wechat|we\s?chat|instagram|insta|facebook|tiktok|tik\s?tok|carousell|shopee|lazada|line\s?id)\b|\b(?:ig|fb|tg|wa)\s*[:@]\s*@?[a-z0-9_.]{3,}/gi;

// A handle on its own: "@fishroom_sg" (not an email, not "@ $5"). The match
// is group 1; the character before it is matched rather than looked behind, as
// older iOS Safari cannot parse a lookbehind (and this file runs in the
// merchant portal too).
const HANDLE = /(?:^|[^a-z0-9._%+-])(@[a-z][a-z0-9_.]{2,})/gi;

const EMAIL = /\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/gi;

// Phone numbers:
//  - international, written with a + ("+65 9423 2556", "+6282281010019");
//  - Singapore 8-digit numbers (6/8/9...) after a phone word ("Call
//    94232556", "Call us @6530 3606", "Tel: 6123 4567"). Without the word an
//    8-digit run is too often a product code or a spec ("Article no
//    81300000", "6000-7000K", "6500 6500 LPH") to treat as a phone;
//  - North American (xxx) xxx-xxxx / xxx-xxx-xxxx.
//
// "Contact us" / "PM us" alone are not flagged: with no number, link or
// handle they read as messaging through FishyHub.
const PHONE_INTL = /\+\d{1,3}[\s.-]?\(?\d{1,4}\)?(?:[\s.-]?\d){6,12}(?!\d)/g;
const PHONE_SG_AFTER_WORD =
  /\b(?:call|contact|tel|telephone|phone|mobile|hp|whatsapp|wa|sms|text|hotline|ring)\b\s*(?:us|me)?\s*(?:no\.?|number)?\s*[:.@-]?\s*(?:\+?65[\s-]?)?[689]\d{3}[\s-]?\d{4}(?!\d)/gi;
const PHONE_NANP = /(?:^|\D)((?:\(\d{3}\)\s?|\d{3}[\s.-])\d{3}[\s.-]\d{4})(?!\d)/g;

const RULES: RegExp[] = [
  CONTACT_HOSTS,
  CONTACT_APPS,
  EMAIL,
  HANDLE,
  PHONE_INTL,
  PHONE_SG_AFTER_WORD,
  PHONE_NANP,
];

const stripHtml = (s: string) =>
  s
    .replace(/<a\b[^>]*href=["']([^"']*)["'][^>]*>/gi, " $1 ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&#64;/g, "@");

/** The contact details found in a title or description (plain text or HTML), in order. */
export function findContactInfo(text: string | null | undefined): string[] {
  if (!text) return [];
  const plain = stripHtml(text);
  const found: string[] = [];
  for (const rule of RULES) {
    for (const m of plain.matchAll(rule)) {
      const hit = (m[1] ?? m[0]).trim();
      if (hit && !found.some((f) => f.includes(hit) || hit.includes(f))) found.push(hit);
    }
  }
  return found;
}

/**
 * The message for a title or description with contact details in it, quoting
 * (up to three of) them so the seller can find and remove them.
 */
export function contactInfoMessage(found: string[]): string {
  const quoted = found
    .slice(0, 3)
    .map((f) => `"${f.length > 40 ? `${f.slice(0, 40)}…` : f}"`)
    .join(", ");
  return `Contact details aren't allowed - remove ${quoted}. Buyers message you through FishyHub chat.`;
}

// ---- Chat --------------------------------------------------------------------
// Chat is stricter than product text in some ways: any link to a site other
// than fishyhub.com is refused (YouTube included), and so is a phone number on
// its own (Singapore, Malaysia, Indonesia) - in a conversation a number is far
// more likely a phone than a product code. It is looser in one: naming an app
// ("I saw it on Shopee", "is it on Instagram?") is fine; only a handle, link
// or "add me on ..."-style request for it is refused. No lookbehinds (older
// iOS Safari), as above.

// Apps people move a chat to. "line", "signal" and "snap" are everyday words
// too, so they count only in an "add me on ..."-style phrase.
const CHAT_APPS =
  "what'?s?\\s?h?app|wa|telegram|tele|tg|wechat|we\\s?chat|line\\s?id|instagram|insta|ig|facebook|fb|messenger|tiktok|tik\\s?tok|carousell|shopee|lazada|viber|snapchat";
const CHAT_APPS_ALL = `${CHAT_APPS}|line|signal|snap`;

// An app with a handle: "IG: fishroom", "telegram @fishroom", "line id = abc12".
const CHAT_APP_HANDLE = new RegExp(
  `\\b(?:${CHAT_APPS})\\s*(?:id|handle|username|user|acc(?:ount)?|no\\.?|number)?\\s*[:@=]\\s*@?[a-z0-9_.+-]{3,}`,
  "gi"
);

// Asking to move to an app: "add me on IG", "message us via WhatsApp", "my insta
// is ...", "WhatsApp me", "find my shop on Shopee".
const CHAT_APP_PHRASE = new RegExp(
  `\\b(?:(?:add|find|follow|dm|pm|message|msg|text|contact|reach|ping|call|chat\\s+with|search(?:\\s+for)?|look\\s+for)\\s+(?:me|us|my\\s+\\w+|our\\s+\\w+)\\s+(?:on|at|via|in|through|thru|using|by)\\s+(?:my\\s+|our\\s+)?(?:${CHAT_APPS_ALL})|(?:my|our)\\s+(?:${CHAT_APPS}|line)\\s+(?:is|id|handle|username|acc(?:ount)?|no\\.?|number)|(?:what'?s?\\s?h?app|telegram|wechat|line|signal|viber)\\s+(?:me|us)|(?:move|switch|talk|chat|continue)\\s+(?:to|on|over\\s+(?:to|on))\\s+(?:${CHAT_APPS}))\\b`,
  "gi"
);

// Links: a scheme or www., or a name ending in a real top-level domain. A
// domain written in Title Case after a full stop ("Thanks.Me too",
// "ok.Info") is a sentence, not a link; see isChatLink.
const ANY_LINK =
  /\b(?:https?:\/\/|www\.)[^\s<>"']+|\b[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)*\.(?:com|net|org|sg|my|id|ph|th|vn|co|io|me|ly|gl|gg|app|shop|store|link|info|biz|xyz|site|online|page|to|cc|tv|us|uk|au|ai)\b(?:\/[^\s<>"']*)?/gi;
const OWN_SITE = /^(?:https?:\/\/)?(?:[a-z0-9-]+\.)*fishyhub\.com(?:[/?#:]|$)/i;
const isChatLink = (hit: string) => {
  if (/^(?:https?:\/\/|www\.)/i.test(hit)) return true;
  const tld = /\.([a-z]+)(?:\/|$)/i.exec(hit)?.[1] ?? "";
  // "Me", "Info": sentence case, so the end of one sentence and the start of
  // the next. "COM", "com": a domain.
  return !/^[A-Z][a-z]+$/.test(tld);
};

// Spelled out: "fishroom dot com", "shop[dot]sg", "john (at) gmail",
// "john at gmail dot com".
const CHAT_DOT_DOMAIN =
  /\b[a-z0-9][a-z0-9-]+\s*[([{]?\s*dot\s*[)\]}]?\s*(?:com|net|org|sg|my|id|co|io|me)\b/gi;
const CHAT_AT_EMAIL =
  /\b[a-z0-9._%+-]{3,}\s*(?:[([{]\s*at\s*[)\]}]|@)\s*(?:gmail|yahoo|hotmail|outlook|icloud|live|msn|ymail|proton(?:mail)?)\b|\b[a-z0-9._%+-]{3,}\s+at\s+(?:gmail|yahoo|hotmail|outlook|icloud|live|msn|ymail|proton(?:mail)?)\s*(?:\.|dot)\s*com\b/gi;

// Phone numbers on their own. The match is group 1; the character before it is
// matched (not looked behind), and a digit, or a "." or "," then a digit,
// after it means it is part of a longer number.
//  - Singapore: 8 digits from 6, 8 or 9, with or without +65 / 65.
const SG_NUMBER = /(?:^|[^\d.,])((?:\+?65[\s-]?)?[689]\d{3}[\s-]?\d{4})(?![\d.,]?\d)/g;
//  - Malaysia mobile: 01x-xxx xxxx or 011-xxxx xxxx, or with 60 / +60 for the 0.
const MY_MOBILE = /(?:^|[^\d.,])((?:\+?60[\s-]?|0)1\d[\s-]?\d{3,4}[\s-]?\d{4})(?![\d.,]?\d)/g;
//  - Malaysia landline: 03-xxxx xxxx (Klang Valley), 0[4-9]-xxx xxxx.
const MY_LANDLINE = /(?:^|[^\d.,])(0[3-9][\s-]?\d{3,4}[\s-]?\d{4})(?![\d.,]?\d)/g;
//  - Indonesia mobile: 08xx..., 10 to 13 digits, or with 62 / +62 for the 0.
const ID_MOBILE = /(?:^|[^\d.,])((?:\+?62[\s-]?|0)8\d(?:[\s-]?\d){7,10})(?![\d.,]?\d)/g;

const CHAT_RULES: RegExp[] = [
  CONTACT_HOSTS,
  CHAT_APP_HANDLE,
  CHAT_APP_PHRASE,
  EMAIL,
  CHAT_AT_EMAIL,
  CHAT_DOT_DOMAIN,
  HANDLE,
  PHONE_INTL,
  PHONE_SG_AFTER_WORD,
  PHONE_NANP,
  SG_NUMBER,
  MY_MOBILE,
  MY_LANDLINE,
  ID_MOBILE,
];

/** Contact details and outside links in a chat message, in order. */
export function findChatContactInfo(text: string | null | undefined): string[] {
  if (!text) return [];
  const found: string[] = [];
  const add = (hit: string) => {
    const h = hit.trim();
    if (h && !found.some((f) => f.includes(h) || h.includes(f))) found.push(h);
  };
  for (const rule of CHAT_RULES) {
    for (const m of text.matchAll(rule)) add(m[1] ?? m[0]);
  }
  for (const m of text.matchAll(ANY_LINK)) {
    if (!OWN_SITE.test(m[0]) && isChatLink(m[0])) add(m[0]);
  }
  return found;
}

/** The message for a chat message refused for contact details or links. */
export function chatContactInfoMessage(found: string[]): string {
  const quoted = found
    .slice(0, 3)
    .map((f) => `"${f.length > 40 ? `${f.slice(0, 40)}…` : f}"`)
    .join(", ");
  return `Contact details and outside links can't be sent (${quoted}). Keep messages and payments on FishyHub so your order stays protected.`;
}
